// src/app/features/auth/reducers/parent.reducer.ts
import { createReducer, on } from '@ngrx/store';
import {
  IParent,
  IParentListFilter,
  IParentListSort,
} from '../../models/parent.model';
import { ParentActions } from './parent.actions';
import { AuthActions } from '../auth/auth.actions';

export interface ParentState {
  currentParent: IParent | null; // Allow both IParent and null
  selectParent: IParent | null; // Allow both IParent and null
  parents: IParent[];
  total: number;
  page: number;
  limit: number;
  loading: boolean;
  error: string | null;
  filter: IParentListFilter;
  sort: IParentListSort;
  activeTab: number;
}

export const initialParentState: ParentState = {
  currentParent: null, // Add this
  parents: [],
  total: 0,
  page: 1,
  limit: 100,
  loading: false,
  error: null,
  filter: {},
  sort: {
    key: 'createdAt', // Or a relevant default sort key for locations
    order: 'desc',
  },
  selectParent: null, // Initialize editParent to nulls
  activeTab: 2, // Initialize activeTab to 0
};

// Helper function to update state
function updateState(
  state: ParentState,
  changes: Partial<ParentState>
): ParentState {
  return { ...state, ...changes };
}

export const parentReducer = createReducer(
  initialParentState,
  // Load Parents
  on(ParentActions.loadParentsList, (state) =>
    updateState(state, { loading: true, error: null })
  ),
  on(ParentActions.loadParentsListSuccess, (state, { response, filter }) =>
    updateState(state, {
      parents: response.data.parentClients.list.map((parent: IParent) => ({
        ...parent,
      })),
      total: response.data.parentClients.total,
      page: response.data.parentClients.page,
      limit: response.data.parentClients.limit,
      filter: filter?.body?.filter ?? {},
      sort: filter?.body?.sort ?? { key: 'createdAt', order: 'desc' },
      // selectParent: null, // Reset selectParent on new load and handle sidebar prevent other options as well
      loading: false,
      error: null,
    })
  ),
  on(ParentActions.loadParentsListFailure, (state, { error }) =>
    updateState(state, { loading: false, error })
  ),

  // For single parent
  on(ParentActions.loadSingleParent, (state) =>
    updateState(state, { loading: true, error: null })
  ),
  on(ParentActions.loadSingleParentSuccess, (state, { parent }) =>
    updateState(state, { currentParent: parent, loading: false, error: null })
  ),
  on(ParentActions.loadSingleParentFailure, (state, { error }) =>
    updateState(state, { loading: false, error })
  ),

  // For select parent
  on(ParentActions.selectParent, (state) =>
    updateState(state, { loading: true, error: null })
  ),
  on(ParentActions.selectParentSuccess, (state, { parent }) =>
    updateState(state, { selectParent: parent, loading: false, error: null })
  ),
  on(ParentActions.selectParentFailure, (state, { error }) =>
    updateState(state, { loading: false, error })
  ),

  // update status
  on(ParentActions.changeParentStatus, (state) =>
    updateState(state, { loading: true, error: null })
  ),
  on(ParentActions.changeParentStatusSuccess, (state) =>
    updateState(state, { loading: false, error: null })
  ),
  on(ParentActions.changeParentStatusFailure, (state, { error }) =>
    updateState(state, { loading: false, error })
  ),

  // Create Parent
  on(ParentActions.createParent, (state) =>
    updateState(state, { loading: true, error: null })
  ),
  on(ParentActions.createParentSuccess, (state, { parent }) =>
    updateState(state, {
      parents: [...state.parents, { ...parent, _id: parent._id }],
      total: state.total + 1,
      loading: false,
      error: null,
    })
  ),
  on(ParentActions.createParentFailure, (state, { error }) =>
    updateState(state, { loading: false, error })
  ),

  // Update Parent
  on(ParentActions.updateParent, (state) =>
    updateState(state, { loading: true, error: null })
  ),
  on(ParentActions.updateParentSuccess, (state) =>
    updateState(state, {
      loading: false,
      error: null,
    })
  ),
  on(ParentActions.updateParentFailure, (state, { error }) =>
    updateState(state, { loading: false, error })
  ),

  // Delete Parent
  on(ParentActions.deleteParent, (state) =>
    updateState(state, { loading: true, error: null })
  ),
  on(ParentActions.deleteParentSuccess, (state) =>
    updateState(state, {
      total: state.total - 1,
      loading: false,
      error: null,
    })
  ),
  on(ParentActions.deleteParentFailure, (state, { error }) =>
    updateState(state, { loading: false, error })
  ),

  // Set Parent Query Params
  on(ParentActions.setParentQueryParams, (state, { params, body }) =>
    updateState(state, {
      page: params?.page ?? state.page,
      limit: params?.limit ?? state.limit,
      filter: body?.filter ? ({ ...body.filter } as IParentListFilter) : {},
      sort: body?.sort
        ? ({ ...body.sort } as IParentListSort)
        : { key: 'createdAt', order: 'desc' },
    })
  ),

  // Clear Current Parent
  on(ParentActions.clearCurrentParent, (state) =>
    updateState(state, { currentParent: null })
  ),

  // Clear Select Parent
  on(ParentActions.clearSelectedParent, (state) =>
    updateState(state, { selectParent: null })
  ),
  on(ParentActions.setActiveTab, (state, { tab }) =>
    ({ ...state, activeTab: tab })
  ),
  on(AuthActions.logout, () => initialParentState)
);
